import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Shield } from 'lucide-react';

export default function RolesPermissions() {
  const [groups, setGroups] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });

  const load = useCallback(async () => {
    try {
      const [g, p] = await Promise.all([API.get('/groups'), API.get('/permissions')]);
      setGroups(g.data);
      setAllPerms(p.data);
    } catch { toast.error('Error cargando datos'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', permissions: [] });
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setForm({ name: g.name, description: g.description || '', permissions: g.permissions || [] });
    setDialogOpen(true);
  };

  const togglePerm = (key) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter(p => p !== key)
        : [...f.permissions, key]
    }));
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('El nombre es obligatorio'); return; }
    try {
      if (editing) {
        await API.put(`/groups/${editing.id}`, form);
        toast.success('Grupo actualizado');
      } else {
        await API.post('/groups', form);
        toast.success('Grupo creado');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (gid) => {
    if (!window.confirm('Eliminar este grupo?')) return;
    try {
      await API.delete(`/groups/${gid}`);
      toast.success('Grupo eliminado');
      load();
    } catch { toast.error('Error eliminando'); }
  };

  const permCategories = {};
  allPerms.forEach(p => {
    const cat = p.key.split('.')[0];
    if (!permCategories[cat]) permCategories[cat] = [];
    permCategories[cat].push(p);
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="roles-permissions-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50">Roles y Permisos</h1>
          <p className="text-sm text-zinc-500">Gestiona los grupos de permisos</p>
        </div>
        <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="create-group-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Grupo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map(g => (
          <Card key={g.id} className="glass-card" data-testid={`group-card-${g.id}`}>
            <CardHeader className="flex flex-row items-start justify-between pb-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-base text-zinc-100">{g.name}</CardTitle>
                  <p className="text-xs text-zinc-500">{g.description}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(g)} data-testid={`edit-group-${g.id}`}>
                  <Pencil className="w-3.5 h-3.5 text-zinc-400" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(g.id)} data-testid={`delete-group-${g.id}`}>
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-1">
                {(g.permissions || []).slice(0, 8).map(p => (
                  <Badge key={p} variant="outline" className="text-xs text-zinc-400 border-white/5">{p}</Badge>
                ))}
                {(g.permissions || []).length > 8 && (
                  <Badge variant="outline" className="text-xs text-amber-500/60 border-amber-500/20">+{g.permissions.length - 8} mas</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-heading">{editing ? 'Editar Grupo' : 'Nuevo Grupo'}</DialogTitle>
            <DialogDescription className="text-zinc-500">Configura nombre, descripcion y permisos del grupo</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Nombre</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="bg-zinc-950/50 border-white/10" data-testid="group-name-input" aria-label="Nombre del grupo" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Descripcion</Label>
                <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="bg-zinc-950/50 border-white/10" aria-label="Descripcion" />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-zinc-300">Permisos</Label>
                <Button variant="ghost" size="sm" className="text-amber-500/70 hover:text-amber-500 text-xs"
                  onClick={() => setForm(f => ({ ...f, permissions: f.permissions.length === allPerms.length ? [] : allPerms.map(p => p.key) }))}
                  data-testid="toggle-all-perms">
                  {form.permissions.length === allPerms.length ? 'Quitar todos' : 'Seleccionar todos'}
                </Button>
              </div>
              {Object.entries(permCategories).map(([cat, perms]) => (
                <div key={cat} className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">{cat}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {perms.map(p => (
                      <label key={p.key} className="flex items-center gap-2 p-2 rounded-md hover:bg-white/5 cursor-pointer transition-colors">
                        <Checkbox checked={form.permissions.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                        <div>
                          <span className="text-sm text-zinc-300">{p.key}</span>
                          <span className="text-xs text-zinc-600 ml-2">{p.description}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200">Cancelar</Button>
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="group-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
